import './Controllers.scss';

import { IconButton, Tooltip, useMediaQuery } from '@material-ui/core';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import SquareDelete from '../../../../../components/SquareDelete/SquareDelete';
import Table from '../../../../../components/Table/Table';
import { selectCurrentPartner } from '../../../../../redux/selectors/authentication';
import { selectControllerList } from '../../../../../redux/selectors/controllers';
import { selectOrganisationAccessPoints, selectOrganisationSites } from '../../../../../redux/selectors/organisation';
import { doGetControllerList, doDeleteController } from '../../../../../redux/thunks/controllers';
import AddControllerDialog from './AddController/AddControllerDialog';
import { Search } from '../../../../../utils/Search';
import ModalAlert from '../../../../../components/Modals/ModalAlert/ModalAlert';
import { configTypeRender } from '../../../../../helpers/renderers';
import { meshConfiguredStateRender, statusText } from '../../../../../utils/Common';
import AddDoors from '../AccessPointTab/AddOrEditAccessPointModal/Steps/AddDoors/AddDoors';
import Modal from '../../../../../components/Modals/Modal';
import SquareEdit from '../../../../../components/SquareEdit/SquareEdit';
import { CloseRounded } from '@material-ui/icons';
import Button from '../../../../../components/Buttons/Button';
import Typography from '../../../../../components/Typography/Typography';
import { colors } from '../../../../../styles/colors';
import SButton from '../../../../../components/SButton/SButton';
import FlexColumn from '../../../../../components/FlexBox/FlexColumn';
import useIsMobile from '../../../../../hooks/useIsMobile';
import MobileTable from '../../../../../components/MobileTable/MobileTable';
import SDialog from '../../../../../components/SDialog/SDialog';
import FlexRow from '../../../../../components/FlexBox/FlexRow';

const Controllers = () => {

  const { customerId } = useParams();
  const dispatch = useDispatch();

  const isSmallScreen = useIsMobile();

  const [currentController, setCurrentController] = useState(null);
  const [showControllerDialog, setShowControllerDialog] = useState(false);
  const [showDeleteControllerDialog, setShowDeleteControllerDialog] = useState(false);
  const { currentUser, controllers, sites } = useSelector((state) => ({
    accessPoints: selectOrganisationAccessPoints(state),
    currentUser: selectCurrentPartner(state),
    controllers: selectControllerList(state),
    sites: selectOrganisationSites(state)
  }));
  const [pagination, setPagination] = useState({ total: 0, currentPage: 1, perPage: 7 });
  const [filters, setFilters] = useState({
    serialNumber: '',
    siteName: '',
    networkName: '',
    configuration: '',
    meshConfiguredState: '',
  });
  const [addDoors, setAddDoors] = useState(false);
  const [sitesMap, setSitesMap] = useState({});
  const [networksMap, setNetworksMap] = useState({});
  const [success, setSuccess] = useState({ show: false, accessPoints: [] })

  useEffect(() => {
    for (let i = 0; i < sites?.length; i++) {
      const site = sites[i];
      setSitesMap(prev => ({
        ...prev,
        [site?.id]: site
      }));

      for (let j = 0; j < site?.networks?.length; j++) {
        const network = site?.networks[j]
        setNetworksMap(prev => ({
          ...prev,
          [network?.id]: network,
        }))
      }
    }
  }, [sites]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, total: controllers.length }))
  }, [controllers])

  const convertToHex = (value) => {
    let hexString = value.toString(16);

    // Ensure the hexadecimal string has at least two characters
    if (hexString.length % 2 !== 0) {
      hexString = '0' + hexString;
    }

    // Split the hexadecimal string into groups of two characters
    const hexGroups = [];
    for (let i = 0; i < hexString.length; i += 2) {
      hexGroups.push(hexString.slice(i, i + 2));
    }

    // Join the groups with periods to get the "xx.xx.xx" format
    const formattedHex = hexGroups.join('.');

    return formattedHex;
  };

  useEffect(() => {
    if (currentUser && customerId) dispatch(doGetControllerList(currentUser.partnerId, customerId));
  }, [currentUser, customerId, dispatch]);

  const checkIfPortIsAvailable = (rowData) => {
    const prodId = rowData?.serialNumber?.substring(0, 4);
    let portsAvailable = true;
    const readerPortCounts = rowData?.readerPortStatus?.filter(rp => rp?.isActive === false)?.length || 0;
    if (readerPortCounts <= 0) {
      portsAvailable = false;
      let availableDownstreamPorts = 0
      for (let i = 0; i < rowData?.downstreamBoardStatus?.length; i++) {
        const downstreamBoard = rowData?.downstreamBoardStatus[i];
        for (let j = 0; j < downstreamBoard?.downstreamBoardPorts?.length; j++) {
          const downstreamPort = downstreamBoard?.downstreamBoardPorts[j];
          if (downstreamPort?.isActive === false) {
            availableDownstreamPorts = availableDownstreamPorts + 1
          }
        }
      }
      if (availableDownstreamPorts > 0) {
        portsAvailable = true
      }
    }

    return portsAvailable
  }

  const columns = useMemo(() => {
    const commonColumns = [
      {
        title: 'Serial Number',
        field: 'serialNumber',
        name: 'serialNumber',
      },
      {
        title: 'Site',
        field: 'siteName',
        name: 'siteName',
      },
      {
        title: 'Network',
        field: 'networkName',
        name: 'networkName',
      },
      {
        title: 'Firmware Version',
        render: (columnProps, rowData) => {
          return rowData?.firmwareVersion ? convertToHex(rowData?.firmwareVersion) : "-";
        },
      },
      {
        title: 'Configuration State',
        render: (columnProps, rowData) => {
          return meshConfiguredStateRender(rowData?.configurationStatus)
        },
      },
      {
        title: 'Connectivity Status',
        render: (columnProps, rowData) => {
          return statusText(rowData.connectivityStatus);
        },
      }
    ]

    const actions = [
      {
        title: 'Actions',
        render: (columnProps, rowData, row, s) => {
          const portsAvailable = checkIfPortIsAvailable(rowData);

          return (
            <FlexRow gap='0.5rem' justifyContent='end'>
              <Tooltip title={!portsAvailable ? "All the ports are already occupied" : ""}>
                <SquareEdit
                  style={{
                    opacity: !portsAvailable ? '0.5' : '1',
                    height: '2rem',
                    width: '2rem'
                  }}
                  onPress={() => {
                    if (portsAvailable) {
                      setCurrentController(rowData);
                      setAddDoors(true);
                    }
                  }}
                />
              </Tooltip>
              <SquareDelete
                style={{
                  height: '2rem',
                  width: '2rem',
                  padding: '0'
                }}
                onPress={() => {
                  setCurrentController(rowData);
                  setShowDeleteControllerDialog(true);
                }}
              />
            </FlexRow>
          );
        },
        renderM: (columnProps, rowData, row, s) => {
          const portsAvailable = checkIfPortIsAvailable(rowData)

          return (
            <FlexRow gap='0.5rem' justifyContent='end'>
              <Tooltip enterTouchDelay={0} title={!portsAvailable ? "All the ports are already occupied" : ""}>
                <SquareEdit
                  style={{
                    opacity: !portsAvailable ? '0.5' : '1',
                    height: '2rem',
                    width: '2rem'
                  }}
                  onPress={() => {
                    if (portsAvailable) {
                      setCurrentController(rowData);
                      setAddDoors(true);
                    }
                  }}
                />
              </Tooltip>
              <SquareDelete
                style={{
                  height: '2rem',
                  width: '2rem',
                  padding: '0'
                }}
                onPress={() => {
                  setCurrentController(rowData);
                  setShowDeleteControllerDialog(true);
                }}
              />
            </FlexRow>
          );
        },
      }
    ]

    return {
      allColumns: [...commonColumns, ...actions],
      commonColumns,
      actions
    }

  }, [checkIfPortIsAvailable]);

  const mobileFiltersConfig = useMemo(() => {
    return [
      { name: "Serial Number", key: "serialNumber", type: "search" },
      { name: "Site Name", key: "siteName", type: "search" },
      { name: "Network", key: "networkName", type: "search" },
    ]
  }, [])

  const hSearchChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const search = {
    onChange: hSearchChange,
  };

  console.log({ controllers })

  const formattedControllers = useMemo(() => {
    return controllers?.map(c => ({
      ...c,
      siteName: sitesMap[c?.siteId]?.name || "-",
      networkName: networksMap[c?.networkId]?.name || "-"
    }))
  }, [controllers, sitesMap, networksMap])

  const filteredData = useCallback(() => {
    let data = formattedControllers
    if (filters.serialNumber !== '') {
      data = Search(data, 'serialNumber', filters.serialNumber);
    }
    if (filters.siteName !== '') {
      data = Search(data, 'siteName', filters.siteName);
    }
    if (filters.networkName !== '') {
      data = Search(data, 'networkName', filters.networkName);
    }
    if (filters.configuration !== '') {
      data = Search(data, 'configuration', filters.configuration);
    }
    if (filters.meshConfiguredState !== '') {
      data = Search(data, 'meshConfiguredState', filters.meshConfiguredState);
    }

    if (!isSmallScreen) {
      const filteredItems = data

      data = filteredItems.slice(
        (pagination.currentPage - 1) * 7,
        pagination.currentPage * pagination.perPage,
      );
    }

    return data;
  }, [filters, pagination.currentPage, pagination.perPage, formattedControllers, isSmallScreen]);

  const changePage = (p) => {
    const { page, per_page } = p;
    setPagination(prev => ({
      ...prev,
      currentPage: page,
      perPage: per_page,
    }))
  };

  const handleDeleteController = async () => {
    await dispatch(
      doDeleteController(currentUser.partnerId, customerId, currentController?.serialNumber, (s) => {
        if (s === 'success') {
          dispatch(doGetControllerList(currentUser.partnerId, customerId));
        }
      }),
    );
    setShowDeleteControllerDialog(false);
  };

  const getObjectsWithSerialNumber = useCallback(
    (serialNumber) => {
      const objects = controllers.filter((accessPoint) => {
        return accessPoint.devices.some((device) => device.serialNumber === serialNumber);
      });
      return [...objects]; // Always return an array
    },
    [controllers],
  );

  const channelOptions = useMemo(() => {
    const toReturn = []
    if (currentController?.readerPortStatus?.length > 0) {
      currentController?.readerPortStatus?.forEach(d => {
        if (d?.isActive === false) {
          toReturn?.push(`Main Board - R${d?.readerPortNumber}`)
        }
      })
    }

    if (currentController?.downstreamBoardStatus?.length > 0) {
      const downstreamBoards = currentController?.downstreamBoardStatus;
      for (let i = 0; i < downstreamBoards?.length; i++) {
        const db = downstreamBoards[i];
        for (let j = 0; j < db?.downstreamBoardPorts?.length; j++) {
          const portDetails = db?.downstreamBoardPorts[j]
          if (portDetails?.isActive === false) {
            toReturn?.push(`Downstream Board ${db?.comPort} - R${portDetails?.readerPortNumber}`)
          }
        }
      }
    }

    return toReturn;

  }, [currentController]);

  const hAddDoorsSucces = (addedDoors = []) => {
    setSuccess({ show: true, accessPoints: addedDoors });
    setAddDoors(false)
  }


  return (
    <Fragment>
      <FlexRow justifyContent='space-between' styles={{ marginBottom: '1.3rem', marginTop: '1.3rem' }}>
        <Typography variant={"h7M"}>Controllers</Typography>
        {/* <SButton
          minWidth
          onClick={() => {
            addControllerClicked();
          }}
          disabled={sites == null || sites.length === 0}
        >
          Add Controller
        </SButton> */}
      </FlexRow>

      <div className="table-container">
        {isSmallScreen ?
          <MobileTable
            columns={columns.commonColumns}
            data={filteredData()}
            actions={columns?.actions}
            filtersConfig={mobileFiltersConfig}
            filters={filters}
            setFilters={setFilters}
            emptyMessage="No Controllers found"
          /> :
          <Table
            columns={columns?.allColumns}
            data={filteredData()}
            pagination={pagination}
            options={{ search }}
            changePage={changePage}
            defaultMessage="No Controllers found"
          />
        }
      </div>

      {showControllerDialog && (
        <AddControllerDialog
          isOpen={showControllerDialog}
          handleClose={() => {
            setShowControllerDialog(false);
          }}
          currentUser={currentUser}
          customerId={customerId}
        />
      )}

      <SDialog
        open={addDoors}
        title='Add Doors'
        isFormDialog={true}
        onClose={() => { setAddDoors(false) }}
      >
        <AddDoors
          readerPortStatus={currentController?.readerPortStatus?.filter(rp => rp?.isActive) || []}
          downstreamBoardStatus={currentController?.downstreamBoardStatus?.flatMap(board =>
            board?.downstreamBoardPorts.map(port => ({
              ...port,
              comPort: board.comPort
            }))
          )?.filter(dp => dp?.isActive) || []}
          accessPoint={{
            controllerDeviceSerialNum: {
              vendorToSpintlySerialNum: currentController?.serialNumber,
            },
            network: {
              id: currentController?.networkId
            },
            controllerType: String(currentController?.controllerType) || "1",
            wireProtocol: Number(currentController?.wireProtocol)
          }}
          partnerId={currentUser?.partnerId}
          customerId={customerId}
          editModeChannelOptions={channelOptions}
          editMode={true}
          onSuccess={hAddDoorsSucces}
        />
      </SDialog>

      <SDialog
        title='Delete Controllers'
        open={showDeleteControllerDialog}
        onClose={() => {
          setShowDeleteControllerDialog(false);
        }}
        secondaryButtonConfig={{
          text: 'Cancel',
          onClick: () => {
            setShowDeleteControllerDialog(false);
          }
        }}
        primaryButtonConfig={{
          text: 'Delete',
          color: 'error',
          onClick: handleDeleteController
        }}
      >
        <Typography variant={"b1M"} style={{ padding: '1.5rem 0' }}>Are you sure you want to delete this controller?
          The access points associated with it will also be deleted.</Typography>
      </SDialog>

      <SDialog
        title='Access Points Created'
        open={success?.show}
        onClose={() => {
          setSuccess(prev => ({ ...prev, show: false }))
        }}
        primaryButtonConfig={{
          text: 'Done',
          onClick: () => { setSuccess(prev => ({ ...prev, show: false })) }
        }}
      >
        <Typography variant={"b1M"} style={{ padding: '1.5rem 0' }}>
          Successfully added access points {success?.accessPoints.length > 2
            ? `${success?.accessPoints.slice(0, -1).map(item => `"${item}"`).join(", ")} and "${success?.accessPoints[success?.accessPoints.length - 1]}"`
            : success?.accessPoints.map(item => `"${item}"`).join(" and ")
          }
        </Typography>
      </SDialog>

    </Fragment>
  );
};

export default Controllers;
